import { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Outlet } from 'react-router';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Loader, Media } from '@/js/libs/ui';
import { AppBarNotification } from '@/js/libs/ui/src/V2';

import { AppSuspense } from 'src/components/AppSuspense';
import ReloadHeader from 'src/components/ReloadHeader';
import ScrollArrow from 'src/components/ScrollArrow';
import { TO_TOP_ARROW_OFFSET } from 'src/constants';
import { useOpenNotification } from 'src/modules/Notification/useOpenNotification';
import { Navigation } from './Navigation';
import { Header } from './Navigation/components';
import { useNavigationContext } from './Navigation/context';
import ScrollToTop from './ScrollToTop';
import { Content, Wrapper } from './styled';

const OutletWrapper = memo(function OutletWrapper() {
  const [t] = useTranslation(['backend', 'common']);
  const { isCollapsedNav, criticalNotificationData, dismissCriticalNotification } = useNavigationContext();
  const isDesktop = useMediaQuery({ query: Media.lgMin });
  const openNotification = useOpenNotification();

  const handleContentClick = useCallbackRef(() => {
    openNotification(criticalNotificationData);
    dismissCriticalNotification();
  });

  return (
    <>
      <Helmet title={t('common:GENERAL_TITLE')} />
      {criticalNotificationData && (
        <AppBarNotification
          type='warning'
          title={criticalNotificationData.title}
          body={criticalNotificationData.plainMessage}
          onClose={dismissCriticalNotification}
          onClick={handleContentClick}
        />
      )}
      <Wrapper $isCollapsedNav={isCollapsedNav}>
        <Navigation />
        <Content $isCollapsedNav={isCollapsedNav}>
          {!isDesktop && <Header />}
          <AppSuspense fallback={<Loader />}>
            <Outlet />
          </AppSuspense>
        </Content>
      </Wrapper>
      <ReloadHeader />
      <ScrollToTop />
      <ScrollArrow offset={TO_TOP_ARROW_OFFSET} />
    </>
  );
});

/**
 * NavigationContext is placed here instead of global Providers because it includes
 * role-based API requests that should only run for authenticated users.
 * Global Providers wrap both authenticated and unauthenticated routes,
 * so placing it there would cause requests to fire on login/authentication pages.
 */
export const Layout = memo(function Layout() {
  return (
    <useNavigationContext.Provider>
      <OutletWrapper />
    </useNavigationContext.Provider>
  );
});
